import type { OutlookCategoryColor } from "@cdt/shared";

export interface CategoryColorTokens {
  background: string;
  foreground: string;
  border: string;
}

/**
 * Official Microsoft Outlook category preset colours (preset0–preset24).
 * Values extracted from the Outlook UI / Graph API colour reference.
 * https://learn.microsoft.com/en-us/graph/api/resources/outlookcategory
 */
const PRESET_BG: Record<OutlookCategoryColor, string> = {
  preset0: "#919191", // None / gray
  preset1: "#E74856", // Red
  preset2: "#E87B23", // Orange
  preset3: "#7E5B3E", // Brown
  preset4: "#F5C518", // Yellow
  preset5: "#4CAF50", // Green
  preset6: "#26A9A9", // Teal
  preset7: "#9C8A3C", // Olive
  preset8: "#2196C8", // Blue
  preset9: "#7B4F9E", // Purple
  preset10: "#C01A76", // Cranberry
  preset11: "#4E79C5", // Steel
  preset12: "#2A5599", // DarkSteel
  preset13: "#7A7A7A", // Gray
  preset14: "#434343", // DarkGray
  preset15: "#212121", // Black
  preset16: "#9C0A00", // DarkRed
  preset17: "#C44E00", // DarkOrange
  preset18: "#5E3F28", // DarkBrown
  preset19: "#B8970A", // DarkYellow
  preset20: "#2E7B2E", // DarkGreen
  preset21: "#1A6363", // DarkTeal
  preset22: "#6B5700", // DarkOlive
  preset23: "#1C5A80", // DarkBlue
  preset24: "#4B3578", // DarkPurple
};

/** Fallback for events without a category. */
const FALLBACK: CategoryColorTokens = {
  background: "#6B7280",
  foreground: "#ffffff",
  border: "#4B5563",
};

/**
 * Calculate perceived luminance (WCAG 2.x simplified formula) for an RGB tuple.
 * Returns a value in [0, 1] where 0 = black and 1 = white.
 */
function luminance(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function darken(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const d = (c: number) => Math.max(0, Math.round(c * (1 - amount)));
  return `#${[d(r), d(g), d(b)].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Returns CSS colour tokens for a given Outlook category preset.
 *
 * The foreground is chosen automatically based on WCAG luminance contrast so
 * event chips remain readable in both light and dark modes regardless of the
 * category colour chosen by the user.
 */
export function getCategoryColors(
  color: OutlookCategoryColor | null | undefined,
): CategoryColorTokens {
  if (!color) return FALLBACK;

  const bg = PRESET_BG[color];
  const [r, g, b] = hexToRgb(bg);
  const lum = luminance(r, g, b);
  const foreground = lum > 0.35 ? "#1a1a1a" : "#ffffff";
  const border = darken(bg, 0.2);

  return { background: bg, foreground, border };
}
